import { useState, forwardRef, useRef, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { tree } from "@/data/tree";
import { TreeNode, AccordionContext } from "./TreeNode";
import { VideoBackground } from "@/components/VideoBackground";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface ExpandedState {
  [depth: number]: string | null;
}

interface TreeListRef {
  collapseAll: () => void;
  getExpandedCount: () => number;
}

type VideoPhase = "showcase" | "showcased" | "stationaryIntro" | "stationaryLoop";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

export const TreeList = forwardRef<TreeListRef>(function TreeList(_, ref) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [phase, setPhase] = useState<VideoPhase>("showcase");
  const [expandedState, setExpandedState] = useState<ExpandedState>(() => {
    const initial: ExpandedState = {};
    initial[0] = "about";
    return initial;
  });
  const expandedCountRef = useRef(0);
  const loopVideoRef = useRef<HTMLVideoElement>(null);
  const stationaryVideoRef = useRef<HTMLVideoElement>(null);

  const uiVisible = phase !== "showcase";

  const showcaseSrc = isMobile
    ? "/videos/DheliriouSLaptopVideoShowcaseMobile.mp4"
    : "/videos/DheliriouSLaptopVideoShowcase.mp4";

  const isShowcase = phase === "showcase" || phase === "showcased";
  const isStationaryIntro = phase === "stationaryIntro";
  const isStationaryLoop = phase === "stationaryLoop";

  const handleShowcaseEnded = useCallback(() => {
    setPhase("showcased");
  }, []);

  const handleStationaryIntroEnded = useCallback(() => {
    setPhase("stationaryLoop");
  }, []);

  useEffect(() => {
    if (phase !== "showcased") return;
    const timer = setTimeout(() => {
      setPhase("stationaryIntro");
    }, 5000);
    return () => clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (!isStationaryIntro) return;
    const video = stationaryVideoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.play();
  }, [isStationaryIntro]);

  useEffect(() => {
    if (!isStationaryLoop) return;
    const video = loopVideoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.play();
    let rafId: number;
    const checkLoop = () => {
      if (video.duration && video.currentTime >= video.duration - 0.15) {
        video.currentTime = 0;
      }
      rafId = requestAnimationFrame(checkLoop);
    };
    rafId = requestAnimationFrame(checkLoop);
    return () => cancelAnimationFrame(rafId);
  }, [isStationaryLoop]);

  useEffect(() => {
    if (phase === "showcased" || isStationaryLoop) {
      const timer = setTimeout(() => {
        setExpandedState((prev) => ({ ...prev, [0]: "expertise" }));
      }, isStationaryLoop ? 0 : 2000);
      return () => clearTimeout(timer);
    }
  }, [phase, isStationaryLoop]);

  const handleToggle = useCallback((id: string, depth: number) => {
    setExpandedState((prev) => {
      const next: ExpandedState = { ...prev };
      if (prev[depth] === id) {
        next[depth] = null;
      } else {
        next[depth] = id;
      }
      return next;
    });
  }, []);

  const getExpandedId = useCallback((depth: number): string | null => {
    return expandedState[depth] ?? null;
  }, [expandedState]);

  const collapseAll = useCallback(() => {
    setExpandedState({});
  }, []);

  const getExpandedCount = () => expandedCountRef.current;

  useEffect(() => {
    const count = Object.values(expandedState).filter(Boolean).length;
    expandedCountRef.current = count;
    window.dispatchEvent(new CustomEvent("tree-expand-change", { detail: { count } }));
  }, [expandedState]);

  if (typeof ref !== "function" && ref) {
    ref.current = { collapseAll, getExpandedCount };
  }

  return (
    <AccordionContext.Provider value={{ getExpandedId, handleToggle }}>
      <div className="min-h-screen flex items-center justify-center px-4 py-20 relative">
        <div
          className="absolute inset-0 overflow-hidden pointer-events-none transition-opacity duration-700"
          style={{ opacity: isShowcase ? 1 : 0 }}
        >
          <VideoBackground
            key="showcase"
            src={showcaseSrc}
            onEnded={handleShowcaseEnded}
          />
        </div>

        <div
          className="absolute inset-0 overflow-hidden pointer-events-none transition-opacity duration-700"
          style={{ opacity: isStationaryIntro ? 1 : 0 }}
        >
          <video
            ref={stationaryVideoRef}
            src="/videos/DheliriouSLaptopStationaryIntro.mp4"
            muted
            playsInline
            preload="auto"
            onEnded={handleStationaryIntroEnded}
            className="w-full h-full object-cover"
          />
        </div>

        <div
          className="absolute inset-0 overflow-hidden pointer-events-none transition-opacity duration-700"
          style={{ opacity: isStationaryLoop ? 1 : 0 }}
        >
          <video
            ref={loopVideoRef}
            src="/videos/DheliriouSLaptopStationaryLoop.mp4"
            muted
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="absolute inset-0 bg-black/40 pointer-events-none" />

        <motion.div
          initial={{ opacity: 0 }}
          animate={uiVisible ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="w-full max-w-2xl relative z-10"
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={uiVisible ? "visible" : "hidden"}
          >
            <motion.div variants={itemVariants} className="text-center mb-10">
              <h1 className="text-3xl md:text-5xl font-light tracking-tight text-white/90">
                Creative Technologist
              </h1>
              <p className="text-sm md:text-base font-light text-white/40 mt-3 tracking-wide">
                Visual production &mdash; Software engineering &mdash; Multimedia systems
              </p>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 md:p-8 backdrop-blur-sm"
            >
              {tree.map((node, index) => (
                <motion.div
                  key={node.id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.12, ease: "easeOut" }}
                >
                  <TreeNode node={node} depth={0} />
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={uiVisible ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.8, delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/30"
        >
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown className="w-6 h-6" />
          </motion.div>
        </motion.div>
      </div>
    </AccordionContext.Provider>
  );
});
